import React from 'react';
import { TextVariant } from '@naf/text';
import { useStateMachine } from 'little-state-machine';
import useSelector from '@redux/typedHooks';
import { formattedPrice } from '@lib/formattedPrice';
import { getProductVariant } from '@lib/getProductVariant';
import { updateAction } from '../../lib/updateAction';
import { ProductTitle } from './ProductTitle';
import * as S from './Summary.styles';

interface FunnelSummaryProps {
  products?: { productId: string; amount: string };
  useOriginalPrice?: boolean;
}

export const FunnelSummary = ({ products, useOriginalPrice = false }: FunnelSummaryProps) => {
  const {
    state: { internationalDrivingPermitForm },
  } = useStateMachine({ updateAction });
  const { productId, amount } = products || { productId: '', amount: '1' };
  const eligibleProducts = useSelector((state) => state.internationalDrivingPermit.data.eligibleProducts);
  const originalEligibleProducts = useSelector(
    (state) => state.internationalDrivingPermit.data.originalEligibleProducts,
  );

  const eligibleProduct = eligibleProducts && eligibleProducts[productId];
  const originalProduct = originalEligibleProducts && originalEligibleProducts[productId];
  const product = (useOriginalPrice && originalProduct) || eligibleProduct;
  const membership = internationalDrivingPermitForm?.membership;
  const membershipProduct =
    !useOriginalPrice && membership?.productId && eligibleProducts ? eligibleProducts[membership.productId] : undefined;
  const price = product?.productAppPrice?.[amount];
  const originalPrice = originalProduct?.productAppPrice?.[amount] || price;
  const membershipPrice = membershipProduct?.productAppPrice?.[membership?.amount || '1'];
  const user = useSelector((state) => state.myMembership.customerInformation.data);
  const isMember = user?.membership?.isValid ?? false;

  // Calculate total price: membership price if selected, otherwise IDP price
  let totalPrice: number | undefined;
  if (membershipProduct && membershipPrice) {
    totalPrice = membershipPrice.itemTotalPrice;
  } else if (useOriginalPrice && originalPrice) {
    totalPrice = originalPrice.itemTotalPrice;
  } else if (price) {
    totalPrice = price.itemTotalPrice;
  }

  const membershipVariant = membershipProduct ? getProductVariant(membershipProduct.productNumber) : undefined;

  return product ? (
    <S.SummaryWrapper>
      <S.Summary>
        <S.SummaryHeading variant={TextVariant.Header3}>Oppsummering</S.SummaryHeading>
        {membershipProduct && membershipPrice && (
          <S.LineGroup>
            <S.LineWrapper>
              <S.LineText variant={TextVariant.ArticleTextHeader}>NAF-medlemskap</S.LineText>
              <S.Price variant={TextVariant.ArticleTextHeader}>
                {formattedPrice(membershipPrice.itemTotalPrice)} kr
              </S.Price>
            </S.LineWrapper>
            {membershipVariant && <S.DetailText variant={TextVariant.ArticleText}>{membershipVariant}</S.DetailText>}
            <S.DetailText variant={TextVariant.ArticleText}>Antall personer: 1</S.DetailText>
          </S.LineGroup>
        )}
        <S.LineGroup>
          <S.LineWrapper>
            <ProductTitle
              productName={product.productName}
              isMember={isMember}
              showNonMemberText={!membershipProduct}
            />
            <S.Price variant={TextVariant.ArticleTextHeader}>
              {originalPrice && formattedPrice(originalPrice.itemTotalPrice)} kr
            </S.Price>
          </S.LineWrapper>
          {membershipProduct && originalPrice && (
            <S.DetailRow>
              <S.DetailText variant={TextVariant.ArticleText}>Velkomstgave til nytt medlem</S.DetailText>
              <S.DetailPrice variant={TextVariant.ArticleText}>
                -{formattedPrice(originalPrice.itemTotalPrice)} kr
              </S.DetailPrice>
            </S.DetailRow>
          )}
        </S.LineGroup>
        <S.LineWrapper $withTopBorder>
          <S.LineText variant={TextVariant.ArticleTextHeader}>Totalt</S.LineText>
          <S.TotalPrice variant={TextVariant.SubHeader}>{totalPrice && formattedPrice(totalPrice)} kr</S.TotalPrice>
        </S.LineWrapper>
        {isMember && <S.LineText variant={TextVariant.Small}>Din medlemspris!</S.LineText>}
      </S.Summary>
    </S.SummaryWrapper>
  ) : null;
};
